import {
    CARTS_FETCH_REQUESTED,
    CARTS_FETCH_SUCCESS,
    CARTS_FETCH_FAILED,
    CART_FETCH_REQUESTED,
    CART_FETCH_SUCCESS,
    CART_FETCH_FAILED,
    CART_ITEM_ADD_REQUESTED,
    CART_ITEM_ADD_SUCCESS,
    CART_ITEM_ADD_FAILED,
    CART_ITEM_UPDATE_REQUESTED,
    CART_ITEM_UPDATE_SUCCESS,
    CART_ITEM_UPDATE_FAILED,
    CART_ITEM_REMOVE_REQUESTED,
    CART_ITEM_REMOVE_SUCCESS,
    CART_ITEM_REMOVE_FAILED,
    CART_CHECKOUT_REQUESTED,
    CART_CHECKOUT_SUCCESS,
    CART_CHECKOUT_FAILED,
    CART_CHECKOUT_CONFLICT,
    CART_NOTICE_DISMISSED,
    CUSTOMER_LOGOUT,
} from '../actions/types';

const INITIAL_STATE = {
    // GET /customers/me/carts (CartSummary[]), para el badge y /carrito
    summaries: [],
    isFetchingSummaries: false,
    summariesLoaded: false,
    // { [storeId]: { cart, isFetching, failed, loaded, pendingItems, notice } }
    byStore: {},
    isAdding: false,
    isCheckingOut: false,
};

const EMPTY_STORE_CART = {
    cart: null,
    isFetching: false,
    failed: false,
    loaded: false,
    // { [cartItemId]: true } mientras hay un PATCH/DELETE en vuelo
    pendingItems: {},
    // mensaje del 409 del checkout ("Los precios de tu carrito cambiaron…")
    notice: null,
};

const updateStore = (state, storeId, changes) => ({
    ...state,
    byStore: {
        ...state.byStore,
        [storeId]: {
            ...EMPTY_STORE_CART,
            ...state.byStore[storeId],
            ...changes,
        },
    },
});

const setPending = (state, storeId, cartItemId, pending) => {
    const current = (state.byStore[storeId] || EMPTY_STORE_CART).pendingItems;
    const pendingItems = { ...current };
    if (pending) pendingItems[cartItemId] = true;
    else delete pendingItems[cartItemId];
    return updateStore(state, storeId, { pendingItems });
};

// Mantiene el resumen del badge en sincronía con el Cart que devolvió la API
// sin otro GET: la API sólo lista carritos con al menos una línea.
const syncSummary = (summaries, cart, { toFront = false } = {}) => {
    const storeId = String(cart.storeId);
    const others = summaries.filter((s) => String(s.storeId) !== storeId);

    if (!cart.cartId || !cart.items || cart.items.length === 0) return others;

    const summary = {
        cartId: cart.cartId,
        storeId: cart.storeId,
        store: {
            storeId: cart.store.storeId,
            name: cart.store.name,
            profileImageUrl: cart.store.profileImageUrl,
        },
        itemCount: cart.itemCount,
        subtotal: cart.subtotal,
    };

    if (toFront) return [summary, ...others];

    const index = summaries.findIndex((s) => String(s.storeId) === storeId);
    if (index === -1) return [summary, ...others];
    const next = [...summaries];
    next[index] = summary;
    return next;
};

const withCart = (state, storeId, cart, extra = {}, syncOptions) => ({
    ...updateStore(state, storeId, {
        cart,
        isFetching: false,
        failed: false,
        loaded: true,
        ...extra,
    }),
    summaries: syncSummary(state.summaries, cart, syncOptions),
});

const cartReducer = (state = INITIAL_STATE, action) => {
    switch (action.type) {
        case CARTS_FETCH_REQUESTED:
            return { ...state, isFetchingSummaries: true };
        case CARTS_FETCH_SUCCESS:
            return {
                ...state,
                summaries: action.payload,
                isFetchingSummaries: false,
                summariesLoaded: true,
            };
        case CARTS_FETCH_FAILED:
            return {
                ...state,
                isFetchingSummaries: false,
                summariesLoaded: true,
            };

        case CART_FETCH_REQUESTED:
            return updateStore(state, action.payload.storeId, {
                isFetching: true,
                failed: false,
            });
        case CART_FETCH_SUCCESS:
            return withCart(state, action.payload.storeId, action.payload.cart);
        case CART_FETCH_FAILED:
            return updateStore(state, action.payload.storeId, {
                isFetching: false,
                failed: true,
                loaded: true,
            });

        case CART_ITEM_ADD_REQUESTED:
            return { ...state, isAdding: true };
        case CART_ITEM_ADD_SUCCESS:
            return {
                ...withCart(
                    state,
                    action.payload.storeId,
                    action.payload.cart,
                    { notice: null },
                    { toFront: true }
                ),
                isAdding: false,
            };
        case CART_ITEM_ADD_FAILED:
            return { ...state, isAdding: false };

        case CART_ITEM_UPDATE_REQUESTED:
        case CART_ITEM_REMOVE_REQUESTED:
            return setPending(
                state,
                action.payload.storeId,
                action.payload.cartItemId,
                true
            );
        case CART_ITEM_UPDATE_SUCCESS:
        case CART_ITEM_REMOVE_SUCCESS: {
            const { storeId, cartItemId, cart } = action.payload;
            return withCart(
                setPending(state, storeId, cartItemId, false),
                storeId,
                cart,
                { notice: null }
            );
        }
        case CART_ITEM_UPDATE_FAILED:
        case CART_ITEM_REMOVE_FAILED:
            return setPending(
                state,
                action.payload.storeId,
                action.payload.cartItemId,
                false
            );

        case CART_CHECKOUT_REQUESTED:
            return { ...state, isCheckingOut: true };
        case CART_CHECKOUT_SUCCESS: {
            // La API marcó el carrito como pedido. El Cart local queda como
            // estaba (la pantalla sigue en "Redirigiendo…" hasta que cambia
            // la página); cualquier pantalla del carrito lo relee al montar.
            const storeId = String(action.payload.storeId);
            return {
                ...state,
                summaries: state.summaries.filter(
                    (s) => String(s.storeId) !== storeId
                ),
                isCheckingOut: false,
            };
        }
        case CART_CHECKOUT_CONFLICT:
            return {
                ...withCart(
                    state,
                    action.payload.storeId,
                    action.payload.cart,
                    {
                        notice: action.payload.message,
                    }
                ),
                isCheckingOut: false,
            };
        case CART_CHECKOUT_FAILED:
            return { ...state, isCheckingOut: false };

        case CART_NOTICE_DISMISSED:
            return updateStore(state, action.payload.storeId, { notice: null });

        case CUSTOMER_LOGOUT:
            return INITIAL_STATE;
        default:
            return state;
    }
};

export default cartReducer;
